import { useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";import { classNames } from "@ct-react/core";
import { useLocaleFormatter } from "@ct-react/locale";
import { AnyArticleDetails, HeatingSystem, isSaleArticle, RentalAccommodationFeatures } from "@shared/graphql";
import { actions, article, pricing } from "../../i18n/sharable-defs";
import { SkeletonableProps, WithTitle } from "../../tools/components";
import SpriteSvg from "../common/svg";
import "./equipment.scss";

type Props = {
  detail: AnyArticleDetails
}

const hasFeatureValue = (value: unknown) => {
  if (value === undefined || value === null || value === 0 || value === "" || value === "0")
    return false;

  if (typeof value === "object" && "amount" in value) {
    const amount = (value as { amount?: unknown }).amount;
    return amount !== undefined && amount !== null && amount !== 0 && amount !== "" && amount !== "0";
  }

  return true;
};

const EquipmentPart = ({loading, title, subTitle, className, data}:WithTitle<SkeletonableProps<Props>>) => {
  const intl = useIntl();
  const { print, transLib } = useLocaleFormatter();

  if (loading || !data)
    return (
      <div className={classNames("equipment-part location", className)}>
        <h2><Skeleton width="33%" /></h2>
        {!!subTitle && <Skeleton containerClassName="sub-title" width="50%" />}
        <Skeleton containerClassName="equipment-part__grid" count={8} inline={true} />
      </div>);

  if(!isSaleArticle(data.detail)){
    return(
      <div className={classNames("equipment-part location", className)}>
        <h2>{title}</h2>
        {!!subTitle &&
          <div className="sub-title">{subTitle}</div>
        }
        <ul className="equipment-part__grid">
          {data.detail.features.withParking && <li><SpriteSvg id="parking"/>{intl.formatMessage(article.parking)}</li>}
          {data.detail.features.withSauna && <li><SpriteSvg id="sauna"/>{intl.formatMessage(article.sauna)}</li>}
          {data.detail.features.withWIFI && <li><SpriteSvg id="wifi"/>{intl.formatMessage(article.wifi)}</li>}
          {(data.detail.features as RentalAccommodationFeatures).isPetCompliant && <li><SpriteSvg id="no-pet"/>{intl.formatMessage(article.no_pet)}</li>}
          {data.detail.features.withLift && <li><SpriteSvg id="elevator"/>{intl.formatMessage(article.elevator)}</li>}
          {data.detail.features.withTV && <li><SpriteSvg id="tv"/>{intl.formatMessage(article.tv)}</li>}
          {(data.detail.features as RentalAccommodationFeatures).isSmokerCompliant && <li><SpriteSvg id="no-smoker"/>{intl.formatMessage(article.no_smoker)}</li>}
          {(data.detail.features as RentalAccommodationFeatures).withHairDryer && <li><SpriteSvg id="hairdryer"/>{intl.formatMessage(article.hairdryer)}</li>}
          {(data.detail.features as RentalAccommodationFeatures).withMicrowave && <li><SpriteSvg id="microwave"/>{intl.formatMessage(article.microwave)}</li>}
          {(data.detail.features as RentalAccommodationFeatures).withRacletteSet || (data.detail.features as RentalAccommodationFeatures).withFondueSet && <li><SpriteSvg id="cheese-set"/>{intl.formatMessage(article.cheese_set)}</li>}
          {data.detail.features.withBalcony && <li><SpriteSvg id="balcony"/>{intl.formatMessage(article.balcony)}</li>}
          {data.detail.features.withWashingMachine && <li><SpriteSvg id="clothes-washer"/>{intl.formatMessage(article.clothes_washer)}</li>}
        </ul>
      </div>);
  }

  if(isSaleArticle(data.detail)){
    console.log("Sale article features", data.detail.features);

    return(
      <div className={classNames("equipment-part sale", className)}>
        <ul className="equipment-part__grid">
          <li><strong>{intl.formatMessage(pricing.price)} : </strong> {data.detail.price.onDemand ?
            intl.formatMessage(pricing.onDemand)
            : print.price(data.detail.price.value, { trailingZeroDisplay: "stripIfInteger" }) + ".-"
            }</li>
          <li><strong>{intl.formatMessage(article.surface)} : </strong>{data.detail.features.livingSpace ?
            data.detail.features.livingSpace + "m²"
            : "-"
          }</li>
          <li><strong>{intl.formatMessage(article.year)} : </strong>{data.detail.features.constructionYear ?
            data.detail.features.constructionYear
            : "-"
          }</li>
          <li><strong>{intl.formatMessage(article.orientation)} : </strong>{data.detail.features.orientation ?
            transLib.match("orientations", data.detail.features.orientation)
            : "-"
          }</li>
          <li><strong>{intl.formatMessage(article.bedroom)} : </strong>{data.detail.features.bedrooms ?
            data.detail.features.bedrooms
            : "-"
          }</li>
          <li><strong>{intl.formatMessage(article.floor)} : </strong>{data.detail.features.floor ?
            data.detail.features.floor.value
            : "-"
          }</li>
          <li><strong>{intl.formatMessage(article.bathroom)} : </strong>{data.detail.features.bathrooms ?
            data.detail.features.bathrooms
            : "-"
          }</li>
          <li><strong>{intl.formatMessage(article.heating)} : </strong>{data.detail.features.heatings ?
            data.detail.features.heatings
              .map((h: HeatingSystem) => transLib.match("heatingSystems", h))
              .join(" - ")
            : intl.formatMessage(actions.no)
          }</li>
          <li><strong>{intl.formatMessage(article.parking)} : </strong>{data.detail.features.withParking ?
            intl.formatMessage(actions.yes)
            : intl.formatMessage(actions.no)
          }</li>
          {hasFeatureValue(data.detail.features.renovationFund) &&
            <li><strong>{intl.formatMessage(article.renovation_fund)} : </strong>{print.price(data.detail.features.renovationFund!, { trailingZeroDisplay: "stripIfInteger" })}</li>
          }
          <li><strong>{intl.formatMessage(article.furnished)} : </strong>{data.detail.features.isFurnished ?
            intl.formatMessage(actions.yes)
            : intl.formatMessage(actions.no)
          }</li>
          <li><strong>{intl.formatMessage(article.forForeigners)} : </strong>{data.detail.features.isSellableToForeigners ?
            intl.formatMessage(actions.yes)
            : intl.formatMessage(actions.no)
          }</li>
          {hasFeatureValue(data.detail.features.charges) &&
            <li><strong>{intl.formatMessage(article.annual_charges)} : </strong>{print.price(data.detail.features.charges!, { trailingZeroDisplay: "stripIfInteger" })}</li>
          }
        </ul>
      </div>);
  }

};


export default EquipmentPart;
